/**
 * Owner-facing cremation tracking on My Bookings. A gentle, read-only timeline of
 * the fulfillment journey (Confirmed → Picked up → Cremation in progress →
 * Ashes ready → Ashes returned), a soft "ashes ready" moment, and the certificate
 * to view/print once the ashes are returned. Self-gates to CREMATION bookings and
 * only fetches once the request has been accepted. Mirrors OwnerWalkIncidents.
 */
import { Check, Loader2 } from 'lucide-react';
import { ImageCarousel } from '@/components/image-carousel';
import { CremationCertificateCard } from '@/components/cremation-certificate-card';
import { use_owner_fulfillment } from '@/features/cremation-fulfillment/hooks';
import {
  stage_label,
  is_burial,
  CREMATION_STAGE_ORDER,
  CREMATION_STAGE_TIMESTAMP,
  type CremationFulfillment,
} from '@/features/cremation-fulfillment/types';
import type { Booking } from '@/features/booking/types';

function format_stamp(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function Timeline({ f }: { f: CremationFulfillment }) {
  const current_idx = CREMATION_STAGE_ORDER.indexOf(f.status);
  return (
    <ol className="flex flex-col" data-testid={`cremation-timeline-${f.booking_id}`}>
      {CREMATION_STAGE_ORDER.map((stage, idx) => {
        const done = idx <= current_idx;
        const is_current = idx === current_idx;
        const stamp = format_stamp(f[CREMATION_STAGE_TIMESTAMP[stage]] as string | null);
        const last = idx === CREMATION_STAGE_ORDER.length - 1;
        return (
          <li key={stage} className="flex gap-3" data-testid={`cremation-timeline-step-${stage}`}>
            <div className="flex flex-col items-center">
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[10px] font-bold ${
                  done ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-card text-muted-foreground'
                }`}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : idx + 1}
              </span>
              {!last && <span className={`w-0.5 flex-1 ${idx < current_idx ? 'bg-primary' : 'bg-border'}`} />}
            </div>
            <div className={last ? '' : 'pb-4'}>
              <p className={`text-sm font-semibold ${is_current ? 'text-primary' : done ? 'text-foreground' : 'text-muted-foreground'}`}>
                {stage_label(stage, f.service_type)}
              </p>
              {stamp && <p className="text-xs text-muted-foreground">{stamp}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function OwnerCremationTracker({ booking }: { booking: Booking }) {
  const not_started =
    booking.status === 'AWAITING_VENDOR' ||
    booking.status === 'PENDING_PAYMENT' ||
    booking.status === 'CANCELLED' ||
    booking.status === 'NO_SHOW';

  const f_q = use_owner_fulfillment(booking.id, booking.module_type === 'CREMATION' && !not_started);

  if (booking.module_type !== 'CREMATION') return null;

  if (not_started) {
    if (booking.status === 'CANCELLED' || booking.status === 'NO_SHOW') return null;
    return (
      <div
        className="basis-full rounded-card border-2 border-brand-outline-variant/20 bg-muted/30 p-3 text-sm text-muted-foreground"
        data-testid={`cremation-awaiting-${booking.id}`}
      >
        We’ve shared your request with the provider. We’ll keep you gently updated here once they accept.
      </div>
    );
  }

  const f = f_q.data;

  return (
    <div className="basis-full" data-testid={`cremation-track-${booking.id}`}>
      {f_q.isLoading ? (
        <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !f ? null : (
        <div className="flex flex-col gap-3 rounded-card border-2 border-brand-outline-variant/20 bg-card p-4 shadow-clay-sm">
          {f.status === 'ASHES_READY' && (
            <p
              className="rounded-input border-2 border-primary/25 bg-primary/[0.06] p-2.5 text-sm text-foreground"
              data-testid={`cremation-ashes-ready-${booking.id}`}
            >
              {is_burial(f.service_type)
                ? 'The resting place is ready. Your provider will be in touch about the farewell.'
                : 'The ashes are ready. Your provider will arrange their gentle return to you.'}
            </p>
          )}

          <Timeline f={f} />

          {/* Provider-shared journey photos (read-only). */}
          {f.photos.length > 0 && (
            <div data-testid={`cremation-photos-${booking.id}`}>
              <ImageCarousel images={f.photos} />
            </div>
          )}

          {f.certificate && (
            <CremationCertificateCard
              cert={f.certificate}
              rootTestId={`owner-certificate-${booking.id}`}
              numberTestId={`owner-certificate-number-${booking.id}`}
              printTestId={`owner-certificate-print-${booking.id}`}
            />
          )}
        </div>
      )}
    </div>
  );
}
